import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Lock } from "lucide-react";
import api from "../lib/api";
import { track } from "../lib/usageTracker";
import { useAuth } from "../context/AuthContext";
import { ActionButton, SecondaryAction } from "./kit";
import LoadFailureNote from "./LoadFailureNote";
import { SectionCard } from "./identityForm";
import { AlertCallout, OUTLINE_ACTION_STYLE } from "./SettingsSignOut";
import {
  ACCESS_PAUSED,
  ACCESS_PAUSED_GUARDIAN,
  ADD_CARD,
  APP_KEEPS_LINE,
  BILLING_DESC,
  BILLING_FAILED_WHAT,
  BILLING_TITLE,
  CHIP_UNKNOWN,
  CHOOSE_A_PLAN,
  GUARDIAN_TITLE,
  KEEP_MY_PLAN,
  NEXT_CHARGE,
  NOT_DELETING,
  NO_PLAN_BODY,
  NO_PLAN_TITLE,
  NO_REFUND_ANNUAL_LINE,
  PLAN_EYEBROW,
  RENEWAL_FAILED,
  RENEWAL_FAILED_GUARDIAN,
  RENEWS,
  accessEndsAt,
  freePeriodAccessEndsAt,
  freePeriodNoticeLine,
  paymentFailedBy,
  paymentFailedByGuardian,
  STOPPED_LINE,
  STOPPING,
  STOP_BILLING,
  STOP_CANCEL,
  STOP_CONFIRM,
  STOP_FAILED,
  STRIPE_HOLDS_LINE,
  UPDATE_CARD,
  UPDATE_CARD_BUSY,
  UPDATE_CARD_FAILED,
  UPDATE_CARD_NOTE,
  billingView,
  guardianLine,
  stopArmed,
  planRunsTo,
  stopExplain,
} from "../lib/billingCopy";

/**
 * Settings › Billing (#1037, Payments 2; D204.2), behind `newUi("pay")` and
 * drawn as `SettingsBilling.dc.html` (design-round-7/payments): a fold in
 * More, closed by default, its chip saying Monthly, Annual, Stopped or No plan.
 *
 * WHAT IT READS AND DOES. `GET /api/billing/status` is read on mount, so the
 * chip is right while the fold is closed. Nothing about a card is shown or
 * held (D200.1). Two actions, both through the API and never Stripe directly:
 *   - Update card: `POST /api/billing/portal` -> Stripe's customer portal.
 *     Cancelling there is cancel-at-period-end and reaches the row by webhook.
 *   - Stop billing: two presses in place (Sign out everywhere's shape), then
 *     `POST /api/billing/stop`. It cancels at period end, deletes nothing,
 *     refunds nothing (D198.2, D192.4).
 * The state shown after a stop is the server's answer, never a guess.
 *
 * GOLD. One per state: Update card while a plan is running or failing to
 * renew, Keep my plan in a stopped plan whose paid period has not ended
 * (D425), Choose a plan with no plan or once a stopped plan's period has
 * ended (D198.3). Stop billing is navy (D198.2). Every failure is on the page, never a toast (D57, D109).
 *
 * AN 11-TO-17 PLAYER (D192.2) sees the plan, when it renews and who pays, and
 * no card control and no Stop billing: those are the guardian's.
 *
 * BILLING NOT CONFIGURED (the API answers 503 `billing_unavailable`): there is
 * nothing to read or change, so the card is not drawn, rather than drawing a
 * load failure that a retry cannot fix.
 */
const panel = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  padding: 12,
  background: "var(--nw-line-faint)",
  border: "1px solid var(--nw-line)",
  borderRadius: 10,
};
const eyebrow = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--nw-ink-muted)",
};
const small = { fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" };
const body = { fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" };

export default function BillingCard({ appearance } = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const guardianPays = user?.age_band === "11_to_17";
  // "pending" (first read, nothing drawn yet) | "loading" (a retry) | "ready" | "failed" | "hidden" (billing is not configured here)
  const [load, setLoad] = useState("pending");
  const [status, setStatus] = useState(null);
  const [stop, setStop] = useState("idle"); // "idle" | "armed" | "stopping"
  const [stopFailed, setStopFailed] = useState(false);
  const [opening, setOpening] = useState(false);
  const [openFailed, setOpenFailed] = useState(false); // Update card and Keep my plan share one portal press
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Returns the status body, or null when it could not be read. `quiet` is a re-read behind a screen that is
   * already drawn (its failure changes nothing); `retry` shows the loading state first. The FIRST read draws
   * nothing until it answers, so a deployment with billing off never flashes a fold that then vanishes.
   */
  const read = useCallback(async ({ quiet = false, retry = false } = {}) => {
    if (retry) setLoad("loading");
    try {
      const { data } = await api.get("/billing/status");
      if (!mounted.current) return null;
      setStatus(data);
      setLoad("ready");
      return data;
    } catch (e) {
      if (!mounted.current) return null;
      if (!quiet) setLoad(e?.response?.status === 503 && e?.response?.data?.code === "billing_unavailable" ? "hidden" : "failed");
      return null;
    }
  }, []);

  useEffect(() => {
    read();
  }, [read]);

  if (load === "hidden" || load === "pending") return null;

  const view = load === "ready" ? billingView(status, { guardianPays }) : null;

  // Update card and Keep my plan (D425) are the same press: the API's portal route, then Stripe's page.
  const openPortal = async () => {
    if (opening) return;
    setOpenFailed(false);
    setOpening(true);
    try {
      const { data } = await api.post("/billing/portal", {});
      if (typeof data?.url === "string" && data.url.startsWith("https://")) {
        window.location.assign(data.url);
        return; // leaving the page: the gold stays busy
      }
      throw new Error("no url");
    } catch {
      if (!mounted.current) return;
      setOpenFailed(true);
      setOpening(false);
    }
  };

  const confirmStop = async () => {
    if (stop === "stopping") return;
    setStopFailed(false);
    setStop("stopping");
    try {
      const { data } = await api.post("/billing/stop", {});
      if (!mounted.current) return;
      if (data?.status !== "stopped") throw new Error("not stopped");
      track("billing.stop");
      setStatus((s) => ({ ...s, status: "stopped", current_period_end: data.current_period_end ?? s?.current_period_end ?? null }));
      setStop("idle");
    } catch (e) {
      if (!mounted.current) return;
      setStop("idle");
      // After ANY failed stop (409, 502, 503, a dropped connection) the plan is read again: a webhook, another
      // tab or a half-finished stop may already have stopped it, and then that is what shows (review of #1592, F3).
      const fresh = await read({ quiet: true });
      if (!mounted.current) return;
      if (fresh && fresh.status !== "active" && fresh.status !== "past_due") return;
      setStopFailed(true);
    }
  };

  const chooseAPlan = () => navigate("/plan");

  const planPanel = (v, { showNext }) => (
    <div style={panel} data-testid="settings-billing-plan">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <span style={eyebrow}>{PLAN_EYEBROW}</span>
          <span data-testid="settings-billing-plan-name" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 17, color: "var(--nw-ink)" }}>
            {v.planName}
          </span>
        </div>
        {v.periodLabel && (
          <span
            data-testid="settings-billing-period"
            style={{ padding: "6px 12px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "var(--nw-ink)", flexShrink: 0 }}
          >
            {v.periodLabel}
          </span>
        )}
      </div>
      {showNext && v.endText && (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 14 }}>
          <span style={{ color: "var(--nw-ink-muted)" }}>{guardianPays ? RENEWS : NEXT_CHARGE}</span>
          <span data-testid="settings-billing-next" style={{ fontWeight: 600, color: "var(--nw-ink)" }}>
            {v.endText}
          </span>
        </div>
      )}
    </div>
  );

  const stripeLines = (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="settings-billing-stripe">
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", ...body }}>
        <Lock size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2, color: "var(--nw-navy)" }} />
        <span>{STRIPE_HOLDS_LINE}</span>
      </div>
      <div style={small} data-testid="settings-billing-app-keeps">
        {APP_KEEPS_LINE}
      </div>
    </div>
  );

  const chooseButton = (
    <ActionButton data-testid="settings-billing-choose-plan" onClick={chooseAPlan}>
      {CHOOSE_A_PLAN}
    </ActionButton>
  );

  const content = () => {
    if (load === "loading") {
      return (
        <div data-testid="settings-billing-loading" aria-busy="true" aria-label="Loading your billing" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[56, 36].map((h) => (
            <div key={h} style={{ height: h, borderRadius: 10, background: "var(--nw-line-faint)" }} />
          ))}
        </div>
      );
    }
    if (load === "failed") {
      return <LoadFailureNote what={BILLING_FAILED_WHAT} onRetry={() => read({ retry: true })} testId="settings-billing-load-failed" tapBox />;
    }
    const v = view;
    if (v.kind === "no-plan") {
      return (
        <>
          <div style={panel} data-testid="settings-billing-no-plan">
            <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-ink)" }}>{NO_PLAN_TITLE}</div>
            <div style={body}>{NO_PLAN_BODY}</div>
          </div>
          {chooseButton}
        </>
      );
    }
    if (v.kind === "stopped") {
      return (
        <>
          {planPanel(v, { showNext: false })}
          <div style={body} data-testid="settings-billing-stopped">
            {STOPPED_LINE}
          </div>
          {v.accessLeft && v.endText ? (
            <>
              <div style={{ ...body, fontWeight: 600 }} data-testid="settings-billing-runs-to">
                {v.pausesText ? (v.noCard ? freePeriodAccessEndsAt(v.pausesText) : accessEndsAt(v.pausesText)) : planRunsTo(v.endText)}
              </div>
              {/* The card and the portal are the guardian's (D192.2): an 11-to-17 player is told when, and has no control. */}
              {!guardianPays && (
                <>
                  <ActionButton
                    data-testid="settings-billing-keep-plan"
                    onClick={() => { track("billing.keep_plan"); openPortal(); }}
                    aria-disabled={opening || undefined}
                    style={opening ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
                  >
                    {opening ? UPDATE_CARD_BUSY : KEEP_MY_PLAN}
                  </ActionButton>
                  <div style={small}>{UPDATE_CARD_NOTE}</div>
                  {openFailed && <AlertCallout testId="settings-billing-keep-error">{UPDATE_CARD_FAILED}</AlertCallout>}
                </>
              )}
            </>
          ) : (
            chooseButton
          )}
        </>
      );
    }
    // running, renewal-failed (access continues while Stripe retries, D196.4), or access-paused (#1691, D473)
    const paused = v.kind === "access-paused";
    return (
      <>
        {/* A failing renewal's period end is the one that has passed: no "next charge" date is drawn for it. */}
        {planPanel(v, { showNext: v.kind === "running" })}
        {v.period === "annual" && v.kind === "running" && (
          <div style={body} data-testid="settings-billing-annual-note">
            {NO_REFUND_ANNUAL_LINE}
          </div>
        )}
        {/* A player who never gave a card is not told a payment failed (#1707 review F2): the free-period words, as on the Dashboard. */}
        {paused && (
          <AlertCallout testId="settings-billing-paused">
            {v.noCard ? freePeriodNoticeLine({ paused: true, guardian: guardianPays }) : guardianPays ? ACCESS_PAUSED_GUARDIAN : ACCESS_PAUSED}
          </AlertCallout>
        )}
        {v.kind === "renewal-failed" && v.noCard && (
          <AlertCallout testId="settings-billing-free-ended">
            {freePeriodNoticeLine({ paused: false, pausesText: v.pausesText, guardian: guardianPays })}
          </AlertCallout>
        )}
        {v.kind === "renewal-failed" && !v.noCard && v.pausesText && (
          <AlertCallout testId="settings-billing-pauses-by">
            {guardianPays ? paymentFailedByGuardian(v.pausesText) : paymentFailedBy(v.pausesText)}
          </AlertCallout>
        )}
        {v.kind === "renewal-failed" && !v.noCard && !v.pausesText && (
          <AlertCallout testId="settings-billing-renewal-failed">{guardianPays ? RENEWAL_FAILED_GUARDIAN : RENEWAL_FAILED}</AlertCallout>
        )}
        {stripeLines}
        {guardianPays ? (
          <div style={{ ...panel, borderLeft: "3px solid var(--nw-navy)" }} data-testid="settings-billing-guardian">
            <div style={eyebrow}>{GUARDIAN_TITLE}</div>
            <div style={body}>{guardianLine(v.masked)}</div>
          </div>
        ) : (
          <>
            <ActionButton
              data-testid="settings-billing-update-card"
              onClick={openPortal}
              aria-disabled={opening || undefined}
              style={opening ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
            >
              {opening ? UPDATE_CARD_BUSY : paused || v.noCard ? ADD_CARD : UPDATE_CARD}
            </ActionButton>
            <div style={small}>{UPDATE_CARD_NOTE}</div>
            {openFailed && <AlertCallout testId="settings-billing-update-error">{UPDATE_CARD_FAILED}</AlertCallout>}

            {/* Paused: the Stop copy promises access to the end of a paid period, which is not true here. Cancelling stays in Stripe's page. */}
            {!paused && <div style={{ height: 1, background: "var(--nw-line)" }} />}

            {!paused && <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="settings-billing-stop-block">
              {stop === "idle" ? (
                <>
                  <div style={body}>{stopExplain(v.endText, v.kind === "renewal-failed")}</div>
                  <SecondaryAction
                    onClick={() => {
                      setStopFailed(false);
                      setStop("armed");
                    }}
                    data-testid="settings-billing-stop"
                    style={OUTLINE_ACTION_STYLE}
                  >
                    {STOP_BILLING}
                  </SecondaryAction>
                </>
              ) : (
                <>
                  <div style={body} data-testid="settings-billing-stop-text">
                    {stopArmed(v.endText, v.kind === "renewal-failed")}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <SecondaryAction
                      onClick={() => setStop("idle")}
                      disabled={stop === "stopping"}
                      data-testid="settings-billing-stop-cancel"
                      style={{ ...OUTLINE_ACTION_STYLE, opacity: stop === "stopping" ? 0.6 : 1 }}
                    >
                      {STOP_CANCEL}
                    </SecondaryAction>
                    <SecondaryAction
                      onClick={confirmStop}
                      disabled={stop === "stopping"}
                      data-testid="settings-billing-stop-confirm"
                      style={{ ...OUTLINE_ACTION_STYLE, opacity: stop === "stopping" ? 0.6 : 1 }}
                    >
                      {stop === "stopping" ? STOPPING : STOP_CONFIRM}
                    </SecondaryAction>
                  </div>
                </>
              )}
              {stopFailed && <AlertCallout testId="settings-billing-stop-error">{STOP_FAILED}</AlertCallout>}
              <div style={small}>{NOT_DELETING}</div>
            </div>}
          </>
        )}
      </>
    );
  };

  return (
    <SectionCard
      icon={CreditCard}
      usageItem="billing"
      title={BILLING_TITLE}
      desc={BILLING_DESC}
      collapsible
      defaultOpen={false}
      summary={view ? view.chip : CHIP_UNKNOWN}
      toggleTestid="settings-billing-toggle"
      testid="settings-billing-card"
      appearance={appearance}
      tapBox={appearance === "r5"}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="settings-billing">
        {content()}
      </div>
    </SectionCard>
  );
}
